import type { Path, SubmitHandler, UseFormProps } from "react-hook-form";
import type { TypeOf, z } from "zod";

import { useState } from "react";
import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";

export interface Step<T extends z.ZodObject<any>> {
	id: string;
	name: string;
	fields?: readonly Path<TypeOf<T>>[];
}

interface MultiStepOptions<T extends z.ZodObject<any>> {
	schema: T;
	steps: Step<T>[];
}

export function useMultiStepForm<T extends z.ZodObject<any>>(
	props: UseFormProps<TypeOf<T>>,
	opts: MultiStepOptions<T>,
	onSubmit: SubmitHandler<TypeOf<T>> = () => {}
) {
	const { schema, steps } = opts;
	const [currentStep, setCurrentStep] = useState(0);
	const [previousStep, setPreviousStep] = useState(0);

	const form = useForm<TypeOf<T>>({
		resolver: zodResolver(schema),
		mode: "onChange",
		...props
	});

	const next = async () => {
		const { fields } = steps[currentStep];
		if (!fields)
			return;

		// Validate only the current step's fields
		const isValid = await form.trigger(fields);

		if (isValid && currentStep < steps.length) {
			if (currentStep === steps.length - 1) {
				// On last step, validate all fields before submitting
				const isFormValid = await form.trigger();
				if (isFormValid) {
					await form.handleSubmit(onSubmit)();
				}
			}
			setPreviousStep(currentStep);
			setCurrentStep(step => step + 1);
		}
	};

	const prev = () => {
		if (currentStep > 0) {
			setPreviousStep(currentStep);
			setCurrentStep(step => step - 1);
		}
	};

	return {
		...form,
		next,
		prev,
		currentStep,
		previousStep
	};
}
